import { isNil, round } from 'lodash';
import { GetCollectionsQuery, AccessType } from '../../../../generated/graphql';
import { SalesType } from '@client/new/common/types';
import { toThumbnailUrl } from '../collection/mappers';
import {
  CollectionStats,
  CollectionsModel,
  LiteCollection,
  LiteCollectionType,
  RawCollection,
} from './collections.domain';
import { ArrayItem } from '@client/new/helpers/types';
import { getSalesPageUrl } from '@client/new/helpers/landingPages';

const getHasOngoingChallenges = (c: RawCollection): boolean => {
  return (c.challenges || []).some((ch) => {
    if (!ch) return false;
    const end = new Date(ch.completionEnd);
    return end.getTime() > Date.now();
  });
};

const getOngoingChallengeDeepLinkURL = (c: RawCollection): string | null => {
  const challenge = (c.challenges || []).find((ch) => {
    if (!ch) return false;
    const end = new Date(ch.completionEnd);
    return end.getTime() > Date.now();
  });

  return challenge?.deepLink || null;
};

export const rawDataToLiteCollection = (
  collections: Array<RawCollection>,
  customURL: string | null,
  profileDeepLink: string | null,
): Array<LiteCollection> =>
  collections.map((c): LiteCollection => {
    return {
      title: c.name || '',
      thumbnailUrl: toThumbnailUrl(c.mediaCover),
      programId: c.id,
      ...toCollectionStats(c.programStats),
      type: toLiteCollectionType(c),
      isLocked: !!c.isLocked,
      salesPageUrl: getSalesPageUrl({
        customURL: customURL || '',
        id: c.id,
        name: c.name || '',
      }),
      hasChallenges: (c.challenges || []).length > 0,
      hasOngoingChallenges: getHasOngoingChallenges(c),
      isVisible: c.isVisible || false,
      customURL: customURL || '',
      profileDeepLink: profileDeepLink || '',
      index: c.index ?? undefined,
      deeplinkUrl:
        c.exclusiveProgramMetadata?.deeplinkUrl || c.deeplinkUrl || undefined,
      accessType: c.accessType,
      challengeDeeplinkURL: getOngoingChallengeDeepLinkURL(c),
      otpPrice: !isNil(c.exclusiveProgramMetadata?.price),
      migrationBundleId: c.migrationBundleId ?? null,
    };
  });

export const getAccessType = (type: SalesType): Array<AccessType> => {
  return type === 'otp'
    ? [AccessType.Exclusive, AccessType.PremiumExclusive]
    : [AccessType.Premium];
};

export type ProgramStatsTiny = NonNullable<
  ArrayItem<GetCollectionsQuery['collections']>['programStats']
>;

export const toCollectionStats = (
  stats: ProgramStatsTiny | null | undefined,
): CollectionStats => ({
  rating: stats?.avgRating ? round(stats.avgRating, 1) : 0,
  begins: stats?.statsBegins ?? 0,
  comments: stats?.statsComments ?? 0,
  completions: stats?.statsCompletions ?? 0,
});

export const toLiteCollectionType = (
  p: Pick<
    ArrayItem<GetCollectionsQuery['collections']>,
    'archivedAt' | 'publishedAt'
  >,
): LiteCollectionType => {
  return p.archivedAt !== null && p.archivedAt !== undefined
    ? 'archived'
    : p.publishedAt !== null && p.publishedAt !== undefined
    ? 'published'
    : 'draft';
};

export const splitCollections = (
  data: LiteCollection[],
): CollectionsModel['collections'] => ({
  draft: data.filter((c) => c.type === 'draft'),
  published: data.filter((c) => c.type === 'published'),
  archived: data.filter((c) => c.type === 'archived'),
});
